import { useCallback } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { useEnabledFeatures } from '../../common/hooks/enabled-features/use-enabled-features';
import { EventQueueView, UnitQueueView } from '@mark43/cad-api';
import { useAddAssignedUnitsApi } from '../../unit-context-menu/use-add-assiged-units-api';
import { useQueueStore } from '../context/use-queue-store';
import { modals, Text } from '@cad-arc';

const DRAG_AND_DROP_STRATEGY = {
    UNIT_TO_UNIT: 'UNIT_TO_UNIT',
    UNIT_TO_EVENT: 'UNIT_TO_EVENT',
    EVENT_TO_UNIT: 'EVENT_TO_UNIT',
} as const;

const ACCEPTED_TYPE = 'ROW';

const getDnDStrategy = (
    dragEntity: string | undefined,
    destinationEntity: string | undefined
): keyof typeof DRAG_AND_DROP_STRATEGY | undefined => {
    if (destinationEntity === 'UNIT' && dragEntity === 'UNIT') {
        return DRAG_AND_DROP_STRATEGY.UNIT_TO_UNIT;
    }

    if (destinationEntity === 'CAD_AGENCY_EVENT' && dragEntity === 'UNIT') {
        return DRAG_AND_DROP_STRATEGY.UNIT_TO_EVENT;
    }

    if (dragEntity === 'CAD_AGENCY_EVENT' && destinationEntity === 'UNIT') {
        return DRAG_AND_DROP_STRATEGY.EVENT_TO_UNIT;
    }
};

type DragData = {
    entityId: number;
    entityType: string;
    eventNumber?: string;
    unitCallSign?: string;
};

type UseDraggableQueueRowArgs = DragData;

type UseDraggableQueueRowReturn = {
    isDropOver: boolean;
    isDragging: boolean;
    elementRef: (elRef: HTMLDivElement | null) => void;
};

export function useDraggableQueueRow({
    entityType,
    entityId,
    eventNumber,
    unitCallSign,
}: UseDraggableQueueRowArgs): UseDraggableQueueRowReturn {
    const addAssignedUnits = useAddAssignedUnitsApi();
    const { listStore } = useQueueStore();
    const {
        dragAndDropDispatch,
        userDragAndDropDispatch,
        confirmUnitAssignmentViaDragDrop,
    } = useEnabledFeatures();

    const confirmUnitAssignment = useCallback(
        async (unitCallSign?: string, eventNumber?: string) => {
            if (!confirmUnitAssignmentViaDragDrop) {
                return true;
            }
            return await modals.confirm({
                title: '',
                body: (
                    <Text>
                        Assign unit {unitCallSign} to event {eventNumber}?
                    </Text>
                ),
            });
        },
        [confirmUnitAssignmentViaDragDrop]
    );

    const handleUnitAssignment = useCallback(
        async ({
            dragId,
            destinationId,
            strategy,
            eventNumber,
            unitCallSign,
        }: {
            dragId: number;
            destinationId: number;
            strategy: keyof typeof DRAG_AND_DROP_STRATEGY | undefined;
            eventNumber?: string;
            unitCallSign?: string;
        }) => {
            if (!dragId || !destinationId || !strategy) return;

            switch (strategy) {
                case DRAG_AND_DROP_STRATEGY.UNIT_TO_UNIT: {
                    const unitQueueView = listStore.getItemById(destinationId) as UnitQueueView;
                    const eventId = unitQueueView?.assignedAgencyEventId;
                    const eventDestinationNumber = unitQueueView?.eventQueueView?.eventNumber;

                    if (eventId) {
                        if (await confirmUnitAssignment(unitCallSign, eventDestinationNumber)) {
                            await addAssignedUnits(
                                {
                                    eventId,
                                    unitIds: [dragId],
                                },
                                true
                            );
                        }
                    }
                    break;
                }
                case DRAG_AND_DROP_STRATEGY.UNIT_TO_EVENT: {
                    const eventDestinationNumber = (listStore.getItemById(
                        destinationId
                    ) as EventQueueView)?.eventNumber;

                    if (await confirmUnitAssignment(unitCallSign, eventDestinationNumber)) {
                        await addAssignedUnits(
                            {
                                eventId: destinationId,
                                unitIds: [dragId],
                            },
                            true
                        );
                    }
                    break;
                }
                case DRAG_AND_DROP_STRATEGY.EVENT_TO_UNIT: {
                    const unitCallSignDestination = (listStore.getItemById(
                        destinationId
                    ) as UnitQueueView)?.callSign;

                    if (await confirmUnitAssignment(unitCallSignDestination, eventNumber)) {
                        await addAssignedUnits(
                            {
                                eventId: dragId,
                                unitIds: [destinationId],
                            },
                            true
                        );
                    }
                    break;
                }
            }
        },
        [addAssignedUnits, confirmUnitAssignment, listStore]
    );

    const [{ isDragging }, drag] = useDrag(
        () => ({
            canDrag: () => dragAndDropDispatch && userDragAndDropDispatch,
            type: ACCEPTED_TYPE,
            item: { entityType, entityId, eventNumber, unitCallSign },
            collect: (monitor) => ({
                isDragging: monitor.isDragging(),
            }),
        }),
        [dragAndDropDispatch, userDragAndDropDispatch]
    );

    const [{ isDropOver }, drop] = useDrop(
        () => ({
            canDrop: () => dragAndDropDispatch && userDragAndDropDispatch,
            accept: ACCEPTED_TYPE,
            drop: async (dragData: DragData) => {
                if (!dragData) return null;

                const strategy = getDnDStrategy(dragData.entityType, entityType);
                const destinationId = entityId;
                const dragId = dragData.entityId;
                const eventNumber = dragData.eventNumber;
                const unitCallSign = dragData.unitCallSign;
                const isDestinationElementSame = destinationId === dragId;

                if (isDestinationElementSame || !strategy) return null;

                handleUnitAssignment({
                    dragId,
                    destinationId,
                    strategy,
                    eventNumber,
                    unitCallSign,
                });
                return dragData;
            },
            collect: (monitor) => ({
                isDropOver: monitor.isOver(),
            }),
        }),
        [handleUnitAssignment, entityType, entityId, dragAndDropDispatch, userDragAndDropDispatch]
    );

    const elementRef = (elRef: HTMLDivElement | null) => {
        if (!dragAndDropDispatch || !userDragAndDropDispatch) {
            return elRef;
        }

        return drop(drag(elRef));
    };

    return {
        elementRef,
        isDragging,
        isDropOver,
    };
}
